import axios from "axios";
import config from "../config";
import type * as types from "../lib/types";

export type Message = {
  id: string;
  turn_id: string;
  chat_room_id: string;
  character_id?: string;
  mob_id?: string;
  content?: string;
  image_url?: string;
  type: "text" | "image";
  generated_by: string;
  llm_model_id: string;
  feedback?: types.Feedback;
  status?: types.Status;
  created_at: string;
};

export type Messages = {
  messages: Message[];
};

export type SendMessageInput = {
  turn_id: string;
  character_id?: string;
  mob_id?: string;
  content?: string;
  image_url?: string;
  generated_by: string;
  llm_model_id: string | null;
  feedback?: types.Feedback;
  status?: types.Status;
};

export type UploadImageInput = {
  turn_id: string;
  character_id?: string;
  mob_id?: string;
  image_data: string; // Base64 encoded
  file_extension: string;
  generated_by: string;
};

export type UploadImageOutput = {
  message_id: string;
  image_url: string;
};

export type ImageInfo = {
  message_id: string;
  image_url: string;
  character_id?: string;
  mob_id?: string;
  created_at: string;
};

export type ListImagesOutput = {
  images: ImageInfo[];
};

export type SendExistingImageInput = {
  turn_id: string;
  character_id?: string;
  mob_id?: string;
  source_message_id: string;
  generated_by: string;
};

export type SendMessageOutput = {
  id: string;
};

export type UpdateFeedbackInput = types.Feedback;

export type UpdateStatusInput = types.Status;

export type UpdateContentInput = {
  content: string;
};

export const sendMessage = async (
  token: string,
  accountId: string,
  chatRoomId: string,
  data: SendMessageInput,
): Promise<SendMessageOutput> => {
  const response = await axios.post<SendMessageOutput>(
    `${config.apiUrl}/accounts/${accountId}/chat-rooms/${chatRoomId}/messages`,
    data,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    },
  );
  return response.data;
};

export const listMessages = async (token: string, chatRoomId: string): Promise<Messages> => {
  const response = await axios.get<Messages>(`${config.apiUrl}/chat-rooms/${chatRoomId}/messages`, {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });
  return response.data;
};

export const updateFeedback = async (
  token: string,
  messageId: string,
  feedback: UpdateFeedbackInput,
): Promise<void> => {
  await axios.put(
    `${config.apiUrl}/messages/${messageId}/feedback/${feedback}`,
    {},
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    },
  );
};

export const updateStatus = async (token: string, messageId: string, status: UpdateStatusInput): Promise<void> => {
  await axios.put(
    `${config.apiUrl}/messages/${messageId}/status/${status}`,
    {},
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    },
  );
};

export const updateContent = async (token: string, messageId: string, content: UpdateContentInput): Promise<void> => {
  await axios.put(`${config.apiUrl}/messages/${messageId}/content`, content, {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });
};

export const uploadImage = async (
  token: string,
  accountId: string,
  chatRoomId: string,
  data: UploadImageInput,
): Promise<UploadImageOutput> => {
  const response = await axios.post<UploadImageOutput>(
    `${config.apiUrl}/accounts/${accountId}/chat-rooms/${chatRoomId}/messages/image`,
    data,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    },
  );
  return response.data;
};

export const listImages = async (token: string, chatRoomId?: string): Promise<ListImagesOutput> => {
  const params = chatRoomId ? `?chat_room_id=${chatRoomId}` : "";
  const response = await axios.get<ListImagesOutput>(`${config.apiUrl}/images${params}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });
  return response.data;
};

export const sendExistingImage = async (
  token: string,
  accountId: string,
  chatRoomId: string,
  data: SendExistingImageInput,
): Promise<SendMessageOutput> => {
  const response = await axios.post<SendMessageOutput>(
    `${config.apiUrl}/accounts/${accountId}/chat-rooms/${chatRoomId}/messages/existing-image`,
    data,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    },
  );
  return response.data;
};
